import styled from '@emotion/styled';
import {
  Country,
  EmailMarketingOption,
  EnableSendRecurringChargeEmailsOption,
  GateGeoCheckInByDistance,
  IsRemainingSpotCountPublicOption,
  Language,
  Location,
  TaxPricingOption,
} from '../types/location';
import { IRow } from '../types/table';
import { Checkbox } from '../components/elements/Checkbox';

export const columns: {
  identifier: string;
  title: string;
}[] = [
  {
    identifier: 'LocationName',
    title: 'Location Name',
  },
  {
    identifier: 'MarianaTekRegion',
    title: 'Mariana Tek Region',
  },
  {
    identifier: 'PrimaryLanguage',
    title: 'Primary Language',
  },
  {
    identifier: 'DefaultLanguage',
    title: 'Default Language',
  },
  {
    identifier: 'Country',
    title: 'Country',
  },
  {
    identifier: 'StreetAddress1',
    title: 'Street Address 1',
  },
  {
    identifier: 'StreetAddress2',
    title: 'Street Address 2',
  },
  {
    identifier: 'City',
    title: 'City',
  },
  {
    identifier: 'StateProvince',
    title: 'State/Province',
  },
  {
    identifier: 'PostZipCode',
    title: 'Post/Zip Code',
  },
  {
    identifier: 'LocationPhoneNumber',
    title: 'Location Phone Number',
  },
  {
    identifier: 'LocationEmail',
    title: 'Location Email',
  },
  {
    identifier: 'StripeChargeBackEmail',
    title: 'Stripe Charge Back Email',
  },
  {
    identifier: 'CurrencyCode',
    title: 'Currency Code',
  },
  {
    identifier: 'DisplayWaitlistPosition',
    title: 'Display Waitlist Position',
  },
  {
    identifier: 'DisplayWaitlistCount',
    title: 'Display Waitlist Count',
  },
  {
    identifier: 'DisplayRemainingSpotCount',
    title: 'Display Remaining Spot Count',
  },
  {
    identifier: 'EnableSpotChanges',
    title: 'Enable Spot Changes',
  },
  {
    identifier: 'ClassPassEnabled',
    title: 'ClassPass Enabled',
  },
  {
    identifier: 'EnableThirdPartySpotChanges',
    title: 'Enable Third-Party Spot Changes',
  },
  {
    identifier: 'DailyPendingReservationLimit',
    title: 'Daily Pending Reservation Limit',
  },
  {
    identifier: 'EnableGeoCheckIn',
    title: 'Enable Mobile Check-In',
  },
  {
    identifier: 'GateGeoCheckInByDistance',
    title: 'Gate Geo Check-In By Distance',
  },
  {
    identifier: 'GeoCheckInDistanceInMeters',
    title: 'Geo Check-In Distance In Meters',
  },
  {
    identifier: 'EnableRoyaltyFeePlatform',
    title: 'Enable Royalty Fee Platform',
  },
  {
    identifier: 'PrimaryClassroom',
    title: 'Primary Classroom',
  },
  {
    identifier: 'EnablePrecheckedEmailMarketing',
    title: 'Enable Prechecked Email Marketing',
  },
  {
    identifier: 'EnableSendRecurringChargeEmails',
    title: 'Enable Recurring Charge Emails',
  },
  {
    identifier: 'LateCancelFee',
    title: 'Late Cancel Fee',
  },
  {
    identifier: 'NoShowFee',
    title: 'No Show Fee',
  },
  {
    identifier: 'UseTaxInclusivePricing',
    title: 'Use Tax Inclusive Pricing',
  },
  {
    identifier: 'BusinessNumber',
    title: 'Business Number',
  },
  {
    identifier: 'StripeAccountId',
    title: 'Stripe Account ID',
  },
  {
    identifier: 'StripeStatementDescriptor',
    title: 'Stripe Statement Descriptor Text',
  },
  {
    identifier: 'PercentageFeeForNonTerminalCharges',
    title: 'Percentage Fee For Non-Terminal Charges',
  },
  {
    identifier: 'FlatFeeForNonTerminalCharges',
    title: 'Flat Fee For Non-Terminal Charges',
  },
  {
    identifier: 'PercentageFeeForTerminalCharges',
    title: 'Percentage Fee For Terminal Charges',
  },
  {
    identifier: 'FlatFeeForTerminalCharges',
    title: 'Flat Fee For Terminal Charges',
  },
];

const getLanguageFromEnum = (value?: string) => {
  if (!value) return undefined;
  const keys = Object.keys(Language) as (keyof typeof Language)[];
  const key = keys.find((k) => Language[k] === value);
  return key;
};

const getCountryFromEnum = (value?: string) => {
  if (!value) return undefined;
  const keys = Object.keys(Country) as (keyof typeof Country)[];
  const key = keys.find((k) => Country[k] === value);
  return key;
};

const getGeoCheckInFromEnum = (value?: string) => {
  if (!value) return undefined;
  const keys = Object.keys(
    GateGeoCheckInByDistance,
  ) as (keyof typeof GateGeoCheckInByDistance)[];
  const key = keys.find((k) => GateGeoCheckInByDistance[k] === value);
  return key;
};

const getPrecheckedEmailFromEnum = (value?: string) => {
  if (!value) return undefined;
  const keys = Object.keys(
    EmailMarketingOption,
  ) as (keyof typeof EmailMarketingOption)[];
  const key = keys.find((k) => EmailMarketingOption[k] === value);
  return key;
};

const geTaxPricingFromEnum = (value?: string) => {
  if (!value) return undefined;
  const keys = Object.keys(
    TaxPricingOption,
  ) as (keyof typeof TaxPricingOption)[];
  const key = keys.find((k) => TaxPricingOption[k] === value);
  return key;
};

const getIsRemainingSpotCountPublicFromEnum = (value?: string) => {
  if (!value) return undefined;
  const keys = Object.keys(
    IsRemainingSpotCountPublicOption,
  ) as (keyof typeof IsRemainingSpotCountPublicOption)[];
  const key = keys.find((k) => IsRemainingSpotCountPublicOption[k] === value);
  return key;
};

const geEnableSendRecurringChargeEmailsFromEnum = (value?: string) => {
  if (!value) return undefined;
  const keys = Object.keys(
    EnableSendRecurringChargeEmailsOption,
  ) as (keyof typeof EnableSendRecurringChargeEmailsOption)[];
  const key = keys.find(
    (k) => EnableSendRecurringChargeEmailsOption[k] === value,
  );
  return key;
};

export const generateTableRows = (uploadedData: Location[]) => {
  const newElements: IRow = {};

  Object.values(uploadedData).forEach((element, index) => {
    const LocationName = (
      <StyledRow className='text-dark-header font-medium'>
        {element?.Name}
      </StyledRow>
    );
    const MarianaTekRegion = <StyledRow>{element?.RegionName}</StyledRow>;
    const PrimaryLanguage = (
      <StyledRow>{getLanguageFromEnum(element?.PrimaryLanguage)}</StyledRow>
    );
    const DefaultLanguage = (
      <StyledRow>{getLanguageFromEnum(element?.DefaultLanguage)}</StyledRow>
    );
    const Country = (
      <StyledRow>{getCountryFromEnum(element?.Country)}</StyledRow>
    );
    const StreetAddress1 = <StyledRow>{element?.AddressLine1}</StyledRow>;
    const StreetAddress2 = <StyledRow>{element?.AddressLine2}</StyledRow>;
    const City = <StyledRow>{element?.AddressCity}</StyledRow>;
    const StateProvince = <StyledRow>{element?.StateProvince}</StyledRow>;
    const PostZipCode = <StyledRow>{element?.PostalCode}</StyledRow>;
    const LocationPhoneNumber = <StyledRow>{element?.PhoneNumber}</StyledRow>;
    const LocationEmail = <StyledRow>{element?.EmailAddress}</StyledRow>;
    const StripeChargeBackEmail = (
      <StyledRow>{element?.ChargeBackEmail}</StyledRow>
    );
    const CurrencyCode = <StyledRow>{element?.CurrencyCode}</StyledRow>;
    const DisplayWaitlistPosition = (
      <StyledRow className='flex w-full items-center'>
        <Checkbox
          checked={element?.IsWaitlistPositionPublic}
          onChange={() => null}
        />
      </StyledRow>
    );
    const DisplayWaitlistCount = (
      <StyledRow>
        <Checkbox
          checked={element?.IsWaitlistCountPublic}
          onChange={() => null}
        />
      </StyledRow>
    );
    const DisplayRemainingSpotCount = (
      <StyledRow>
        <StyledRow>
          {getIsRemainingSpotCountPublicFromEnum(
            element?.IsRemainingSpotCountPublic,
          )}
        </StyledRow>
      </StyledRow>
    );
    const EnableSpotChanges = (
      <StyledRow>
        <Checkbox checked={element?.EnableChangeSpots} onChange={() => null} />
      </StyledRow>
    );
    const ClassPassEnabled = (
      <StyledRow>
        <Checkbox checked={element?.EnableClasspass} onChange={() => null} />
      </StyledRow>
    );
    const EnableThirdPartySpotChanges = (
      <StyledRow>
        <Checkbox
          checked={element?.EnableThirdPartyChangeSpots}
          onChange={() => null}
        />
      </StyledRow>
    );
    const DailyPendingReservationLimit = (
      <StyledRow>{element?.DailyReservationLimit}</StyledRow>
    );
    const EnableGeoCheckIn = (
      <StyledRow>
        <Checkbox checked={element?.EnableGeoCheckIn} onChange={() => null} />
      </StyledRow>
    );
    const GateGeoCheckInByDistance = (
      <StyledRow>
        {getGeoCheckInFromEnum(element?.GateGeoCheckInByDistance)}
      </StyledRow>
    );
    const GeoCheckInDistanceInMeters = (
      <StyledRow>{element?.GeoCheckInDistance}</StyledRow>
    );
    const EnableRoyaltyFeePlatform = (
      <StyledRow>
        <Checkbox
          checked={element?.EnableRoyaltyFeePlatform}
          onChange={() => null}
        />
      </StyledRow>
    );
    const EnableSendRecurringChargeEmails = (
      <StyledRow>
        {geEnableSendRecurringChargeEmailsFromEnum(
          element?.EnableSendRecurringChargeEmails,
        )}
      </StyledRow>
    );
    const PrimaryClassroom = <StyledRow>{element?.PrimaryClassroom}</StyledRow>;
    const EnablePrecheckedEmailMarketing = (
      <StyledRow>
        {getPrecheckedEmailFromEnum(
          element?.EnablePreCheckedNewsletterSubscription,
        )}
      </StyledRow>
    );
    const LateCancelFee = <StyledRow>{element?.LateCancelFee}</StyledRow>;
    const NoShowFee = <StyledRow>{element?.NoShowFee}</StyledRow>;
    const UseTaxInclusivePricing = (
      <StyledRow>
        {geTaxPricingFromEnum(element?.UseTaxInclusivePricing)}
      </StyledRow>
    );
    const BusinessNumber = <StyledRow>{element?.BusinessNumber}</StyledRow>;

    const StripeAccountId = <StyledRow>{element?.StripeAccountId}</StyledRow>;
    const StripeStatementDescriptor = (
      <StyledRow>{element?.StripeStatementDescriptor}</StyledRow>
    );
    const PercentageFeeForNonTerminalCharges = (
      <StyledRow>{element?.PercentageFee}</StyledRow>
    );
    const FlatFeeForNonTerminalCharges = (
      <StyledRow>{element?.FlatFee}</StyledRow>
    );
    const PercentageFeeForTerminalCharges = (
      <StyledRow>{element?.TerminalPercentageFee}</StyledRow>
    );
    const FlatFeeForTerminalCharges = (
      <StyledRow>{element?.TerminalFlatFee}</StyledRow>
    );

    newElements[index] = {
      LocationName,
      MarianaTekRegion,
      PrimaryLanguage,
      DefaultLanguage,
      Country,
      StreetAddress1,
      StreetAddress2,
      City,
      StateProvince,
      PostZipCode,
      LocationPhoneNumber,
      LocationEmail,
      StripeChargeBackEmail,
      CurrencyCode,
      DisplayWaitlistPosition,
      DisplayWaitlistCount,
      DisplayRemainingSpotCount,
      EnableSpotChanges,
      ClassPassEnabled,
      EnableThirdPartySpotChanges,
      DailyPendingReservationLimit,
      EnableGeoCheckIn,
      GateGeoCheckInByDistance,
      GeoCheckInDistanceInMeters,
      EnableRoyaltyFeePlatform,
      PrimaryClassroom,
      EnablePrecheckedEmailMarketing,
      EnableSendRecurringChargeEmails,
      LateCancelFee,
      NoShowFee,
      UseTaxInclusivePricing,
      BusinessNumber,
      StripeAccountId,
      StripeStatementDescriptor,
      PercentageFeeForNonTerminalCharges,
      FlatFeeForNonTerminalCharges,
      PercentageFeeForTerminalCharges,
      FlatFeeForTerminalCharges,
    };
  });

  return newElements;
};

const StyledRow = styled.div`
  width: max-content;
`;
